import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { useNotification } from '../context/NotificationContext';
import { useUsers } from '../context/UsersContext';
import { useBatchValidationErrors, useCheckTypes } from '../hooks/useMasterData';
import SearchableDropdown from '../components/SearchableDropdown';
import RowValidationErrorsCell from '../components/RowValidationErrorsCell';
import Tooltip from '../components/Tooltip';
import UserTimestamp from '../components/UserTimestamp';
import UploadProgressCard from '../components/UploadProgressCard';
import { MdOutlineVisibility, MdOutlineEdit } from 'react-icons/md';
import { Trash2, ArrowUpCircle } from 'lucide-react';
import { formatDateUS } from '../utils/dateUtils';
import { resolveUploadJobName } from '../utils/uploadJobName';
import USDateInput from '../components/USDateInput';
import * as XLSX from 'xlsx';
import './CheckUpload.css';
import './CheckDetails.css';

// ─── helpers ──────────────────────────────────────────────────────────────────

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount || 0);

const formatDateTime = (dt) => {
  if (!dt) return '-';
  return new Date(dt).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

const formatRowNumber = (n) => {
  if (n == null) return '-';
  const num = Number(n);
  return isNaN(num) ? String(n) : num >= 1000 ? String(num) : String(num).padStart(3, '0');
};

const getRowStatusStyle = (rowStatus) => {
  const map = {
    VALID: { background: '#d1fae5', color: '#059669' },
    INVALID: { background: '#fee2e2', color: '#dc2626' },
    PENDING: { background: '#fef3c7', color: '#d97706' },
    PROMOTED: { background: '#dbeafe', color: '#2563eb' },
  };
  return map[rowStatus] || { background: '#f3f4f6', color: '#6b7280' };
};

const getJobStatusStyle = (status) => {
  const map = {
    READY_TO_REVIEW: { background: '#d1fae5', color: '#059669' }, // same as Check Upload READY_TO_PROMOTE
    READY_TO_PROMOTE: { background: '#d1fae5', color: '#059669' },
    CORRECTION_REQUIRED: { background: '#fee2e2', color: '#dc2626' },
    COMPLETED: { background: '#d1fae5', color: '#059669' },
    FAILED: { background: '#fee2e2', color: '#dc2626' },
    PENDING: { background: '#fef3c7', color: '#d97706' },
  };
  return map[status] || { background: '#f3f4f6', color: '#6b7280' };
};

const formatJobStatus = (status) => {
  if (!status) return 'PENDING';
  return status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
};

const BATCH_SOURCE_OPTIONS = ['Athena', 'Centricity'];

const normalizeBatchType = (t) => {
  const u = String(t || '').trim().toUpperCase();
  return u === 'AUTO' ? 'AUTO' : 'MANUAL';
};

const normalizeBatchSource = (s) => {
  const v = String(s ?? '').trim();
  return BATCH_SOURCE_OPTIONS.includes(v) ? v : 'Athena';
};

/** Initial editable promote payload (mirrors API body under `batch` + top-level check/practice/location). */
const buildPromoteFormFromBatch = (batch, job) => {
  const correction = Boolean(batch.correctionBatch ?? batch.isCorrectionBatch ?? false);
  return {
    checkNumber: batch.systemCheckNumber ?? batch.checkNumber ?? '',
    practiceCode: job.practiceCode ?? '',
    locationCode: job.locationCode ?? '',
    batchRunNumber: batch.batchRunNumber ?? batch.runNumber ?? '',
    batchNumber: batch.batchNumber ?? '',
    invoiceNumber: batch.invoiceNumber ?? batch.checkNumber ?? '',
    edmNumber: batch.edmNumber ?? '',
    batchDate: batch.batchDate ?? batch.sheetCreateDate ?? '',
    batchType: normalizeBatchType(batch.batchType),
    batchSource: normalizeBatchSource(batch.batchSource),
    batchAmount: (() => {
      const n = batch.batchAmount ?? batch.payments ?? batch.total;
      return n != null && n !== '' ? String(n) : '';
    })(),
    batchNotes: batch.batchNotes ?? batch.comments ?? '',
    correctionBatch: correction,
    isCorrectionBatch: correction,
  };
};

// ─── sub-components ───────────────────────────────────────────────────────────

const TruncatedText = ({ text, maxLength = 25 }) => {
  if (!text) return <span>-</span>;
  const should = text.length > maxLength;
  const display = should ? text.substring(0, maxLength) + '...' : text;
  return should ? (
    <Tooltip text={text} position="bottom">
      <span className="truncated-text">{display}</span>
    </Tooltip>
  ) : <span>{display}</span>;
};

const TruncatedName = ({ text, maxLength = 12 }) => {
  if (!text) return <span>-</span>;
  const should = text.length > maxLength;
  const display = should ? text.substring(0, maxLength) + '...' : text;
  return should ? (
    <Tooltip text={text} position="bottom">
      <span className="truncated-text">{display}</span>
    </Tooltip>
  ) : <span>{display}</span>;
};

// Read-only label+value pair used in view modal
const DetailRow = ({ label, value, wide }) => (
  <div style={{ gridColumn: wide ? '1 / -1' : 'auto', marginBottom: '12px' }}>
    <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>{label}</div>
    <div style={{ fontSize: '14px', color: '#111827', wordBreak: 'break-word' }}>{value || <span style={{ color: '#9ca3af' }}>—</span>}</div>
  </div>
);

// Compact errors block shown in the popup
const ErrorsBlock = ({ validationErrors, validationErrorInfo }) => {
  const hasErrors = validationErrors && validationErrors.length > 0;
  const hasInfo = !!validationErrorInfo;
  if (!hasErrors && !hasInfo) return <span style={{ color: '#9ca3af' }}>—</span>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {hasErrors && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '8px 12px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#dc2626', textTransform: 'uppercase', marginBottom: '6px' }}>Validation Errors</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 16px' }}>
            {validationErrors.map((e, i) => (
              <div key={i} style={{ fontSize: '13px', color: '#991b1b', display: 'flex', alignItems: 'flex-start', gap: '5px' }}>
                <span style={{ color: '#dc2626', marginTop: '1px', flexShrink: 0 }}>•</span>
                <span>{e}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {hasInfo && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', padding: '8px 12px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#d97706', textTransform: 'uppercase', marginBottom: '4px' }}>Info</div>
          <div style={{ fontSize: '13px', color: '#92400e' }}>{validationErrorInfo}</div>
        </div>
      )}
    </div>
  );
};

// ─── main component ───────────────────────────────────────────────────────────

const EMPTY_EDIT = {
  grp: '', status: '', ini: '', type: '', bkDepositDate: '', sheetCreateDate: '',
  description: '', txnsCount: '', charges: '', payments: '', adjustments: '', total: '',
  name: '', telephone: '', batchType: 'MANUAL', checkType: '', checkNumber: '',
  batchSource: 'Athena', runNumber: '', comments: '',
  batchNumber: '', edmNumber: '', invoiceNumber: '', correctionBatch: false,
};

const BatchValidation = () => {
  const { showError, showSuccess } = useNotification();
  const { getUserById, getUserName } = useUsers();
  const { data: batchErrorTypes, loading: batchErrorTypesLoading } = useBatchValidationErrors(true);
  const { data: checkTypes, loading: checkTypesLoading } = useCheckTypes(true);

  // Dashboard
  const [dashboardJobs, setDashboardJobs] = useState([]);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobSearch, setJobSearch] = useState('');
  const [dashboardPage, setDashboardPage] = useState(0);
  const [dashboardTotalElements, setDashboardTotalElements] = useState(0);
  const [dashboardTotalPages, setDashboardTotalPages] = useState(0);
  const [dashboardHasMore, setDashboardHasMore] = useState(false);
  const [dashboardPageInputValue, setDashboardPageInputValue] = useState('1');

  // Upload
  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [jobName, setJobName] = useState('');
  const [practiceCode, setPracticeCode] = useState('');
  const [locationCode, setLocationCode] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState({
    active: false,
    percent: 0,
    stageIndex: 0,
    stageLabel: 'Preparing',
    helperText: '',
    indeterminate: false,
  });

  // Practice / location options
  const [practices, setPractices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loadingPractices, setLoadingPractices] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [selectedPracticeId, setSelectedPracticeId] = useState('');

  // Batches
  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filter
  const [filterErrors, setFilterErrors] = useState([]);
  const [batchSearch, setBatchSearch] = useState('');

  // Sort
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');

  // View modal
  const [viewingBatch, setViewingBatch] = useState(null);

  // Edit modal
  const [editingBatch, setEditingBatch] = useState(null);
  const [editFormData, setEditFormData] = useState(EMPTY_EDIT);
  const [savingEdit, setSavingEdit] = useState(false);

  // Delete batch row
  const [deletingBatchRow, setDeletingBatchRow] = useState(null);
  const [deletingRow, setDeletingRow] = useState(false);
  const [promotingBatchId, setPromotingBatchId] = useState(null);
  const [promoteModalBatch, setPromoteModalBatch] = useState(null);
  const [promoteForm, setPromoteForm] = useState(null);

  // Job-level actions
  const [revalidating, setRevalidating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);

  // Fetch practices
  useEffect(() => {
    const fetch = async () => {
      setLoadingPractices(true);
      try {
        const data = await api.getAllPractices();
        setPractices((Array.isArray(data) ? data : []).filter(p => p.isActive !== false));
      } catch (e) { console.error(e); } finally { setLoadingPractices(false); }
    };
    fetch();
  }, []);

  // Fetch locations when practice changes
  useEffect(() => {
    if (!selectedPracticeId) { setLocations([]); setLocationCode(''); return; }
    const fetch = async () => {
      setLoadingLocations(true);
      try {
        const data = await api.getPracticeLocations(selectedPracticeId);
        setLocations((Array.isArray(data) ? data : []).filter(l => l.isActive !== false));
      } catch (e) { console.error(e); setLocations([]); } finally { setLoadingLocations(false); }
    };
    fetch();
  }, [selectedPracticeId]);

  // ── API fetchers ─────────────────────────────────────────────────────────────

  const fetchDashboard = useCallback(async (page = 0, searchText = '') => {
    setLoadingDashboard(true);
    try {
      const search = String(searchText || '').trim();
      const response = await api.getBatchValidationJobs({
        page,
        size: 50,
        ...(search ? { jobName: search } : {}),
      });
      setDashboardJobs(response?.items || []);
      const totalPagesCount = response?.totalPages || 0;
      setDashboardTotalElements(response?.totalElements || 0);
      setDashboardTotalPages(totalPagesCount);
      setDashboardHasMore(page < totalPagesCount - 1);
      setDashboardPage(page);
      setDashboardPageInputValue((page + 1).toString());
    } catch (err) {
      const msg = err.data?.message || err.message || 'Failed to load jobs.';
      showError(msg);
    } finally { setLoadingDashboard(false); }
  }, [showError]);

  const fetchJobBatches = useCallback(async (jobId, page = 0, errorSearches = []) => {
    setLoadingBatches(true);
    try {
      const [jobDetails, response] = await Promise.all([
        api.getBatchValidationJob(jobId),
        api.getBatchValidationJobBatches(jobId, { page, size: 50, errorSearches }),
      ]);
      setSelectedJob(jobDetails);
      setBatches(response.items || []);
      setTotalElements(response.totalElements || 0);
      setTotalPages(response.totalPages || 0);
      setCurrentPage(page);
    } catch (err) {
      const msg = err.data?.message || err.message || 'Failed to load batches.';
      showError(msg);
    } finally { setLoadingBatches(false); }
  }, [showError]);

  useEffect(() => { fetchDashboard(dashboardPage, jobSearch); }, [fetchDashboard, dashboardPage, jobSearch]);

  useEffect(() => {
    setDashboardPage(0);
    setDashboardPageInputValue('1');
  }, [jobSearch]);

  // ── File handling ────────────────────────────────────────────────────────────

  const handleDrag = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const isValidFileType = (f) =>
    ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']
      .includes(f.type) || f.name.endsWith('.csv') || f.name.endsWith('.xlsx');

  const processSelectedFile = async (f) => {
    if (!isValidFileType(f)) { showError('Please select a CSV or XLSX file'); setFile(null); return; }
    if (f.size > 10 * 1024 * 1024) {
      showError(`File size is ${(f.size / (1024 * 1024)).toFixed(2)} MB. Maximum allowed is 10 MB.`);
      setFile(null); return;
    }
    setFile(f);
  };

  const handleDrop = async (e) => {
    e.preventDefault(); e.stopPropagation(); setDragActive(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) await processSelectedFile(dropped);
  };

  const handleFileInput = async (e) => {
    const selected = e.target.files?.[0];
    if (selected) await processSelectedFile(selected);
    e.target.value = '';
  };

  const resetUploadModal = () => {
    setFile(null); setJobName(''); setPracticeCode(''); setLocationCode('');
    setSelectedPracticeId('');
    setUploadProgress((prev) => ({ ...prev, active: false }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = async () => {
    if (!file) return;
    if (!practiceCode) { showError('Practice is required'); return; }
    if (!locationCode) { showError('Location is required'); return; }
    setUploading(true);
    setUploadProgress({
      active: true,
      percent: 10,
      stageIndex: 0,
      stageLabel: 'Preparing file',
      helperText: 'Preparing payload and metadata...',
      indeterminate: false,
    });
    try {
      setUploadProgress({
        active: true,
        percent: 30,
        stageIndex: 1,
        stageLabel: 'Validating input',
        helperText: 'Checking selected practice and location...',
        indeterminate: false,
      });
      const resolvedJobName = resolveUploadJobName(jobName, { practiceCode, locationCode });
      const response = await api.createBatchValidationJob(file, practiceCode, locationCode, resolvedJobName, {
        onUploadProgress: (progressEvent) => {
          const uploadPercent = progressEvent?.progress;
          const mapped = uploadPercent == null
            ? 62
            : Math.min(80, Math.max(45, 45 + Math.round((uploadPercent / 100) * 35)));
          setUploadProgress({
            active: true,
            percent: mapped,
            stageIndex: 2,
            stageLabel: 'Uploading file',
            helperText: uploadPercent == null
              ? 'Uploading records for validation...'
              : `Uploading... ${uploadPercent}% complete`,
            indeterminate: uploadPercent == null,
          });
        },
      });
      setUploadProgress({
        active: true,
        percent: 92,
        stageIndex: 3,
        stageLabel: 'Finalizing validation',
        helperText: 'Completing server-side validation...',
        indeterminate: true,
      });
      showSuccess('File uploaded successfully!');
      setShowUploadModal(false); resetUploadModal();
      await fetchDashboard(dashboardPage, jobSearch);
      if (response.job) await fetchJobBatches(response.job.jobId, 0);
    } catch (err) {
      showError(err.data?.message || err.message || 'Failed to upload file.');
      setUploadProgress({
        active: true,
        percent: 100,
        stageIndex: 3,
        stageLabel: 'Upload failed',
        helperText: err.data?.message || err.message || 'Failed to upload file.',
        indeterminate: false,
      });
    } finally { setUploading(false); }
  };

  const isProcessing = revalidating || deleting || exporting;
  const processingMessage = revalidating ? 'Re-validating batches...'
    : exporting ? 'Exporting report...'
    : deleting ? 'Deleting job...'
    : '';

  // ── Job navigation ───────────────────────────────────────────────────────────

  const handleSelectJob = async (job) => {
    setSelectedJob(job); setFilterErrors([]);
    await fetchJobBatches(job.jobId, 0, []);
  };

  const handlePreviousPage = () => {
    if (currentPage > 0 && selectedJob) fetchJobBatches(selectedJob.jobId, currentPage - 1, filterErrors);
  };
  const handleNextPage = () => {
    if (currentPage < totalPages - 1 && selectedJob) fetchJobBatches(selectedJob.jobId, currentPage + 1, filterErrors);
  };

  // ── Filter ───────────────────────────────────────────────────────────────────

  const handleFilterChange = (val) => {
    const newErrors = val ? [val] : [];
    setFilterErrors(newErrors);
    if (selectedJob) fetchJobBatches(selectedJob.jobId, 0, newErrors);
  };

  const handleBatchSearchChange = (e) => {
    setBatchSearch(e.target.value);
  };

  // ── Sort ─────────────────────────────────────────────────────────────────────

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getFilteredAndSortedBatches = () => {
    let list = batches;
    // Client-side search: filter by batchSearch on current page data
    if (batchSearch && batchSearch.trim()) {
      const q = batchSearch.trim().toLowerCase();
      list = list.filter(b => {
        const searchable = [
          b.grp, b.batchNumber, b.status, b.ini, b.type, b.batchType,
          b.checkNumber, b.systemCheckNumber, b.runNumber, b.name,
          b.comments, b.batchSource,
          ...(b.validationErrors || []),
          b.validationErrorInfo,
          b.bkDepositDate, b.sheetCreateDate,
          b.payments != null ? String(b.payments) : '',
          b.charges != null ? String(b.charges) : '',
          b.total != null ? String(b.total) : '',
        ].filter(Boolean).join(' ').toLowerCase();
        return searchable.includes(q);
      });
    }
    if (!sortField) return list;
    return [...list].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === 'bkDepositDate' || sortField === 'sheetCreateDate') {
        aVal = aVal ? new Date(aVal) : new Date(0);
        bVal = bVal ? new Date(bVal) : new Date(0);
      } else if (sortField === 'payments') {
        aVal = aVal ?? 0;
        bVal = bVal ?? 0;
      } else if (sortField === 'sheetRowNumber') {
        aVal = Number(aVal ?? 0);
        bVal = Number(bVal ?? 0);
      } else {
        aVal = aVal ?? '';
        bVal = bVal ?? '';
        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const SortArrow = ({ field }) => {
    if (sortField !== field) return null;
    return <span style={{ marginLeft: '4px', fontSize: '11px', color: '#6366f1' }}>{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  // ── View modal ───────────────────────────────────────────────────────────────

  const handleViewBatch = (batch) => setViewingBatch(batch);

  // ── Edit modal ───────────────────────────────────────────────────────────────

  const handleEditBatch = (batch) => {
    setEditingBatch(batch);
    const correction = Boolean(batch.correctionBatch ?? batch.isCorrectionBatch ?? false);
    setEditFormData({
      grp: batch.grp || '',
      status: batch.status || '',
      ini: batch.ini || '',
      type: batch.type || '',
      bkDepositDate: batch.bkDepositDate || '',
      sheetCreateDate: batch.sheetCreateDate || '',
      description: batch.description || '',
      txnsCount: batch.txnsCount ?? '',
      charges: batch.charges ?? '',
      payments: batch.payments ?? '',
      adjustments: batch.adjustments ?? '',
      total: batch.total ?? '',
      name: batch.name || '',
      telephone: batch.telephone || '',
      batchType: normalizeBatchType(batch.batchType),
      checkType: batch.checkType || '',
      checkNumber: batch.checkNumber || '',
      batchSource: normalizeBatchSource(batch.batchSource),
      runNumber: batch.runNumber || '',
      comments: batch.comments || '',
      batchNumber: batch.batchNumber || '',
      edmNumber: batch.edmNumber || '',
      invoiceNumber: batch.invoiceNumber || '',
      correctionBatch: correction,
    });
  };

  const handleEditFormChange = useCallback((field, value) => {
    setEditFormData((prev) => {
      const newData = { ...prev, [field]: value };
      if (field === 'batchType' && value === 'MANUAL') {
        newData.runNumber = '';
      }
      if (field === 'batchType' && value === 'AUTO') {
        newData.edmNumber = '';
      }
      if (field === 'correctionBatch') {
        newData.correctionBatch = value;
      }
      return newData;
    });
  }, []);

  const handleCancelEdit = () => { setEditingBatch(null); setEditFormData(EMPTY_EDIT); };

  const handleSaveEdit = async () => {
    if (!editingBatch || !selectedJob) return;
    setSavingEdit(true);
    try {
      const payload = {
        grp: editFormData.grp || null,
        status: editFormData.status || null,
        ini: editFormData.ini || null,
        type: editFormData.type || null,
        bkDepositDate: editFormData.bkDepositDate || null,
        sheetCreateDate: editFormData.sheetCreateDate || null,
        description: editFormData.description || null,
        txnsCount: editFormData.txnsCount !== '' ? Number(editFormData.txnsCount) : null,
        charges: editFormData.charges !== '' ? Number(editFormData.charges) : null,
        payments: editFormData.payments !== '' ? Number(editFormData.payments) : null,
        adjustments: editFormData.adjustments !== '' ? Number(editFormData.adjustments) : null,
        total: editFormData.total !== '' ? Number(editFormData.total) : null,
        name: editFormData.name || null,
        telephone: editFormData.telephone || null,
        batchType: editFormData.batchType || null,
        checkType: editFormData.checkType || null,
        checkNumber: editFormData.checkNumber || null,
        batchSource: editFormData.batchSource || 'Athena',
        runNumber: editFormData.runNumber || null,
        batchNumber: editFormData.batchNumber || null,
        edmNumber: editFormData.edmNumber || null,
        invoiceNumber: editFormData.invoiceNumber || null,
        correctionBatch: !!editFormData.correctionBatch,
        isCorrectionBatch: !!editFormData.correctionBatch,
        comments: editFormData.comments || null,
      };
      await api.updateBatchValidationStagingBatch(selectedJob.jobId, editingBatch.stagingBatchId, payload);
      // Auto re-validate after save
      const updatedJob = await api.revalidateBatchValidationJob(selectedJob.jobId);
      setSelectedJob(updatedJob);
      showSuccess('Batch updated and re-validated!');
      setEditingBatch(null); setEditFormData(EMPTY_EDIT);
      await fetchJobBatches(selectedJob.jobId, currentPage, filterErrors);
    } catch (err) {
      showError(err?.data?.message || err?.message || 'Failed to update batch.');
    } finally { setSavingEdit(false); }
  };

  // ── Delete single batch row ──────────────────────────────────────────────────

  const handleDeleteRowClick = (batch) => setDeletingBatchRow(batch);

  const handleDeleteRowConfirm = async () => {
    if (!deletingBatchRow || !selectedJob) return;
    setDeletingRow(true);
    try {
      await api.deleteBatchValidationStagingBatch(selectedJob.jobId, deletingBatchRow.stagingBatchId);
      showSuccess('Batch row deleted successfully!');
      setDeletingBatchRow(null);
      await fetchJobBatches(selectedJob.jobId, currentPage, filterErrors);
    } catch (err) {
      showError(err.data?.message || err.message || 'Failed to delete batch row.');
    } finally { setDeletingRow(false); }
  };

  const handlePromoteFormChange = useCallback((field, value) => {
    setPromoteForm((prev) => {
      if (!prev) return prev;
      const newData = { ...prev, [field]: value };
      if (field === 'batchType' && value === 'MANUAL') {
        newData.batchRunNumber = '';
      }
      if (field === 'batchType' && value === 'AUTO') {
        newData.edmNumber = '';
      }
      if (field === 'correctionBatch') {
        newData.correctionBatch = value;
        newData.isCorrectionBatch = value;
      }
      return newData;
    });
  }, []);

  const openPromoteModal = (batch) => {
    if (!selectedJob) return;
    setPromoteModalBatch(batch);
    setPromoteForm(buildPromoteFormFromBatch(batch, selectedJob));
  };

  const closePromoteModal = () => {
    if (promotingBatchId) return;
    setPromoteModalBatch(null);
    setPromoteForm(null);
  };

  const handleConfirmPromoteBatch = async () => {
    if (!selectedJob || !promoteModalBatch || !promoteForm) return;
    const batch = promoteModalBatch;
    const f = promoteForm;
    setPromotingBatchId(batch.stagingBatchId);
    try {
      const payload = {
        checkNumber: f.checkNumber,
        practiceCode: f.practiceCode,
        locationCode: f.locationCode,
        batch: {
          activityContext: {
            source: selectedJob.source || 'BATCH_VALIDATION',
            jobId: selectedJob.jobId,
            jobName: selectedJob.jobName || '',
            stagingBatchId: batch.stagingBatchId,
          },
          batchRunNumber: f.batchRunNumber,
          batchNumber: f.batchNumber,
          invoiceNumber: f.invoiceNumber,
          edmNumber: f.edmNumber,
          batchDate: f.batchDate || null,
          batchType: f.batchType,
          batchSource: f.batchSource || 'Athena',
          batchAmount: Number(f.batchAmount) || 0,
          batchNotes: f.batchNotes,
          correctionBatch: !!f.correctionBatch,
          isCorrectionBatch: !!f.correctionBatch,
        },
      };
      await api.promoteBatchValidationStagingBatch(selectedJob.jobId, batch.stagingBatchId, payload);
      showSuccess('Batch promoted successfully!');
      closePromoteModal();
      await fetchJobBatches(selectedJob.jobId, currentPage, filterErrors);
    } catch (err) {
      const msg = err?.data?.message || err?.message || String(err);
      showError(msg);
    } finally {
      setPromotingBatchId(null);
    }
  };

  // ── Re-validate ──────────────────────────────────────────────────────────────

  const handleRevalidate = async () => {
    if (!selectedJob) return;
    setRevalidating(true);
    try {
      const updatedJob = await api.revalidateBatchValidationJob(selectedJob.jobId);
      setSelectedJob(updatedJob);
      showSuccess('Job re-validated successfully!');
      await fetchJobBatches(selectedJob.jobId, currentPage, filterErrors);
    } catch (err) {
      const msg = err.data?.message || err.message || 'Failed to re-validate job.';
      showError(msg);
    } finally { setRevalidating(false); }
  };

  // ── Export report ────────────────────────────────────────────────────────────

  const handleDownloadTemplate = () => {
    const templateData = [
      {
        'Grp': '',
        'Batch #': '',
        'Status': '',
        'Ini': '',
        'Type': '',
        'Bk Dep Dt': '',
        'Create Dt': '',
        'Description': '',
        '# Txns': '',
        'Charges': '',
        'Payments': '',
        'Adjustments': '',
        'Total': '',
        'Name': '',
        'Telephone': '',
        'Batch Type': '',
        'Check Type': '',
        'Check number': '',
        'Batch Source': '',
        'Run Number': '',
        'Comments': '',
      }
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(templateData);
    ws['!cols'] = [
      { wch: 8 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 10 },
      { wch: 12 }, { wch: 12 }, { wch: 25 }, { wch: 8 }, { wch: 12 },
      { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 10 }, { wch: 20 }, { wch: 14 },
      { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 12 }, { wch: 28 },
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'Batch Template');
    XLSX.writeFile(wb, 'BatchValidation_Template.xlsx');
  };

  const handleExportReport = async () => {
    if (!selectedJob) return;
    setExporting(true);
    try {
      const blob = await api.exportBatchValidationReport(selectedJob.jobId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedJob.jobName || 'BatchValidation'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      showSuccess('Report exported successfully!');
    } catch (err) {
      const msg = err?.data?.message || err?.message || 'Failed to export report.';
      showError(msg);
    } finally { setExporting(false); }
  };

  // ── Delete job ───────────────────────────────────────────────────────────────

  const handleDeleteClick = (job, e) => { e.stopPropagation(); setJobToDelete(job); setShowDeleteConfirm(true); };

  const handleDeleteConfirm = async () => {
    if (!jobToDelete) return;
    setDeleting(true);
    try {
      await api.deleteBatchValidationJob(jobToDelete.jobId);
      showSuccess(`Job "${jobToDelete.jobName || 'Unnamed Job'}" deleted successfully!`);
      if (selectedJob?.jobId === jobToDelete.jobId) { setSelectedJob(null); setBatches([]); }
      await fetchDashboard(dashboardPage, jobSearch);
      setShowDeleteConfirm(false); setJobToDelete(null);
    } catch (err) {
      showError(err.data?.message || err.message || 'Failed to delete job.');
    } finally { setDeleting(false); }
  };


  // ── Derived ──────────────────────────────────────────────────────────────────

  const practiceOptions = [
    { value: '', label: 'Select Practice' },
    ...practices.map(p => ({ value: p.practiceId || p.id, label: `${p.name} (${p.code})`, code: p.code })),
  ];
  const locationOptions = [
    { value: '', label: 'Select Location' },
    ...locations.map(l => ({ value: l.code, label: `${l.name} (${l.code})` })),
  ];
  const errorFilterOptions = [
    { value: '', label: 'All Errors' },
    ...(batchErrorTypes || []).map(e => ({ value: e.code, label: e.name })),
  ];

  const displayedBatches = !loadingBatches ? getFilteredAndSortedBatches() : [];
  const startIndex = currentPage * 50 + 1;
  const endIndex = Math.min((currentPage + 1) * 50, totalElements);
  const handleDashboardPreviousPage = () => {
    if (dashboardPage > 0 && !loadingDashboard) {
      setDashboardPage((prev) => prev - 1);
    }
  };

  const handleDashboardNextPage = () => {
    if (dashboardHasMore && !loadingDashboard) {
      setDashboardPage((prev) => prev + 1);
    }
  };

  const handleDashboardPageInputChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setDashboardPageInputValue(value);
    }
  };

  const handleDashboardGoToPage = () => {
    if (dashboardPageInputValue === '' || loadingDashboard) return;
    const pageNum = parseInt(dashboardPageInputValue, 10);
    const maxPage = dashboardTotalPages > 0 ? dashboardTotalPages - 1 : 0;
    if (pageNum >= 1 && pageNum <= maxPage + 1) {
      setDashboardPage(pageNum - 1);
    } else {
      setDashboardPageInputValue((dashboardPage + 1).toString());
    }
  };

  const handleDashboardPageInputKeyPress = (e) => {
    if (e.key === 'Enter') handleDashboardGoToPage();
  };
  const showingText = batchSearch && batchSearch.trim()
    ? `${displayedBatches.length} of ${batches.length} batches on this page`
    : `Showing ${startIndex}–${endIndex} of ${totalElements} batches`;

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div className="check-upload-page">
      {/* Processing overlay */}
      {isProcessing && (
        <div className="processing-overlay">
          <div className="processing-content">
            <div className="processing-spinner">
              <svg className="spinner" viewBox="0 0 50 50">
                <circle className="path" cx="25" cy="25" r="20" fill="none" strokeWidth="4" />
              </svg>
            </div>
            <h3 className="processing-title">{processingMessage}</h3>
            <p className="processing-subtitle">Please wait, this may take a few moments...</p>
          </div>
        </div>
      )}

      {!selectedJob && (
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <h1 className="page-title" style={{ margin: 0 }}>Batch Validation</h1>
            <p className="page-subtitle" style={{ margin: 0 }}>Validate batches from CSV/XLSX files against the database</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              className="btn-cancel"
              onClick={handleDownloadTemplate}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <path d="M3 17C3 18.1046 3.89543 19 5 19H15C16.1046 19 17 18.1046 17 17V13M13 10L10 13M10 13L7 10M10 13V1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Download Template
            </button>
            <button className="btn-upload-small" onClick={() => setShowUploadModal(true)} disabled={isProcessing}>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" style={{ marginRight: '6px' }}>
                <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 13H8M12 9V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Batch Validation Upload
            </button>
            <Tooltip text="Upload a CSV/XLSX batch file to validate against database records" position="bottom">
              <div className="info-icon-wrapper">
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M10 14V10M10 6H10.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
            </Tooltip>
          </div>
        </div>
      )}

      {/* ── Upload Modal ─────────────────────────────────────────────────────── */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => { if (!uploading) { setShowUploadModal(false); resetUploadModal(); } }}>
          <div className="upload-modal-content" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#111827' }}>Upload Batch Validation File</h2>
              <button onClick={() => { if (!uploading) { setShowUploadModal(false); resetUploadModal(); } }} disabled={uploading}
                style={{ background: 'none', border: 'none', fontSize: '24px', color: '#6b7280', cursor: uploading ? 'not-allowed' : 'pointer', padding: '0', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px' }}>×</button>
            </div>
            <div className="upload-section-modal">
              <div className={`upload-zone ${dragActive ? 'drag-active' : ''} ${file ? 'has-file' : ''}`}
                onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}>
                <input ref={fileInputRef} type="file" id="bv-file-input" accept=".csv,.xlsx" onChange={handleFileInput} style={{ display: 'none' }} />
                {!file ? (
                  <>
                    <div className="upload-icon">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                        <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M16 13H8M12 9V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <div className="upload-text"><strong>Click to select CSV or XLSX file</strong><span>or drag and drop here</span></div>
                    <label htmlFor="bv-file-input" className="upload-label">Select File</label>
                  </>
                ) : (
                  <div className="file-selected">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                      <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <div className="file-info"><strong>{file.name}</strong><span>{(file.size / 1024).toFixed(2)} KB</span></div>
                    <button className="btn-remove" onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} disabled={isProcessing}>Remove</button>
                  </div>
                )}
              </div>
              {file && (
                <div style={{ marginTop: '20px' }}>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>Job Name (Optional)</label>
                    <input type="text" value={jobName} onChange={e => setJobName(e.target.value)} placeholder="Enter job name"
                      style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Practice <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <SearchableDropdown
                      options={loadingPractices ? [{ value: '', label: 'Loading...' }] : practiceOptions}
                      value={selectedPracticeId}
                      onChange={(val) => {
                        setSelectedPracticeId(val);
                        const found = practices.find(p => (p.practiceId || p.id) === val);
                        setPracticeCode(found?.code || ''); setLocationCode('');
                      }}
                      placeholder="Select Practice"
                    />
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Location <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <SearchableDropdown
                      options={loadingLocations ? [{ value: '', label: 'Loading...' }] : locationOptions}
                      value={locationCode} onChange={setLocationCode}
                      placeholder={!selectedPracticeId ? 'Select practice first' : 'Select Location'}
                      disabled={!selectedPracticeId || loadingLocations}
                    />
                  </div>
                  <div className="upload-actions">
                    <button className="btn-cancel" onClick={resetUploadModal} disabled={isProcessing || uploading}>Cancel</button>
                    <button className="btn-upload" onClick={handleUpload} disabled={uploading || isProcessing || !file || !practiceCode || !locationCode}>
                      {uploading ? 'Uploading...' : 'Upload & Validate'}
                    </button>
                  </div>
                  {uploadProgress.active && (
                    <UploadProgressCard
                      title="Batch validation upload"
                      stageIndex={uploadProgress.stageIndex}
                      stageLabel={uploadProgress.stageLabel}
                      helperText={uploadProgress.helperText}
                      percent={uploadProgress.percent}
                      indeterminate={uploadProgress.indeterminate}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Dashboard View ───────────────────────────────────────────────────── */}
      {!selectedJob && (
        <div>
          <div className="filters-section jobs-search-section">
            <div className="search-bar">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="2" />
                <path d="M15 15L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <input
                type="text"
                value={jobSearch}
                onChange={(e) => setJobSearch(e.target.value)}
                placeholder="Search by job name, file name, practice, or location..."
              />
            </div>
          </div>
          <div className="jobs-section" style={{ marginTop: '0' }}>
            <h2 className="section-title">Validation Jobs</h2>
            {loadingDashboard ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>Loading jobs...</div>
            ) : dashboardJobs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>No validation jobs found</div>
            ) : (
              <div className="jobs-table-wrapper">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <button className="btn-cancel" disabled={dashboardPage === 0 || loadingDashboard} onClick={handleDashboardPreviousPage} title="Previous page" aria-label="Previous page" style={{ padding: '6px 10px', minWidth: '34px' }}>
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                      <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>
                    Showing {dashboardTotalElements === 0 ? 0 : dashboardPage * 50 + 1} - {Math.min((dashboardPage + 1) * 50, dashboardTotalElements)} of {dashboardTotalElements}
                  </div>
                  {dashboardTotalPages > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>Page:</span>
                      <input
                        type="text"
                        value={dashboardPageInputValue}
                        onChange={handleDashboardPageInputChange}
                        onKeyPress={handleDashboardPageInputKeyPress}
                        onBlur={handleDashboardGoToPage}
                        style={{ width: '46px', height: '26px', border: '1px solid #d1d5db', borderRadius: '4px', textAlign: 'center', fontSize: '12px' }}
                      />
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>of {dashboardTotalPages}</span>
                    </div>
                  )}
                  <button className="btn-cancel" disabled={!dashboardHasMore || loadingDashboard} onClick={handleDashboardNextPage} title="Next page" aria-label="Next page" style={{ padding: '6px 10px', minWidth: '34px' }}>
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                      <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
                <table className="jobs-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                      {['Job Name', 'File Name', 'Practice', 'Location', 'Status', 'Total', 'Valid', 'Invalid', 'Created', 'Updated', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardJobs.map(job => (
                      <tr key={job.jobId}
                        style={{ borderBottom: '1px solid #e5e7eb', cursor: isProcessing ? 'not-allowed' : 'pointer', opacity: isProcessing ? 0.6 : 1 }}
                        onClick={() => !isProcessing && handleSelectJob(job)}>
                        <td style={{ padding: '12px' }}><TruncatedName text={job.jobName || 'Unnamed Job'} /></td>
                        <td style={{ padding: '12px' }}><TruncatedName text={job.uploadedFileName || '-'} /></td>
                        <td style={{ padding: '12px' }}>{job.practiceCode || '-'}</td>
                        <td style={{ padding: '12px' }}>{job.locationCode || '-'}</td>
                        <td style={{ padding: '12px' }}>
                          <span className="job-status-badge" style={getJobStatusStyle(job.status)}>
                            {formatJobStatus(job.status)}
                          </span>
                        </td>
                        <td style={{ padding: '12px' }}>{job.totalRows || 0}</td>
                        <td style={{ padding: '12px', color: '#059669' }}>{job.validRows || 0}</td>
                        <td style={{ padding: '12px', color: '#dc2626' }}>{job.invalidRows || 0}</td>
                        <td style={{ padding: '12px' }}>
                          <UserTimestamp userId={job.createdBy} dateTime={job.createdAt} action="created" getUserName={getUserName} getUserById={getUserById} />
                        </td>
                        <td style={{ padding: '12px' }}>
                          <UserTimestamp userId={job.updatedBy} dateTime={job.updatedAt} action="updated" getUserName={getUserName} getUserById={getUserById} />
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <button style={{ padding: '6px 8px', display: 'flex', alignItems: 'center', background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', color: '#374151', cursor: isProcessing ? 'not-allowed' : 'pointer' }}
                              onClick={e => { e.stopPropagation(); handleSelectJob(job); }} disabled={isProcessing} title="View">
                              <MdOutlineVisibility size={16} />
                            </button>
                            <button style={{ padding: '6px 8px', color: '#dc2626', border: '1px solid #dc2626', background: 'white', display: 'flex', alignItems: 'center', borderRadius: '6px', cursor: isProcessing ? 'not-allowed' : 'pointer' }}
                              onClick={e => handleDeleteClick(job, e)} disabled={isProcessing || deleting} title="Delete job">
                              <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                                <path d="M3 6H5H17M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2C10.5304 2 11.0391 2.21071 11.4142 2.58579C11.7893 2.96086 12 3.46957 12 4V6M15 6V16C15 16.5304 14.7893 17.0391 14.4142 17.4142C14.0391 17.7893 13.5304 18 13 18H7C6.46957 18 5.96086 17.7893 5.58579 17.4142C5.21071 17.0391 5 16.5304 5 16V6H15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Job Detail View ──────────────────────────────────────────────────── */}
      {selectedJob && (
        <div style={{ minWidth: 0 }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
              <button className="btn-cancel" onClick={() => { setSelectedJob(null); setBatches([]); setFilterErrors([]); setBatchSearch(''); }} disabled={isProcessing} style={{ flexShrink: 0 }}>
                ← Back
              </button>
              <div style={{ minWidth: 0 }}>
                <h2 style={{ margin: '0', fontSize: '20px', fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedJob.jobName || 'Unnamed Job'}</h2>
                <p style={{ margin: '2px 0 0 0', color: '#6b7280', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {selectedJob.uploadedFileName} &bull; Practice: <strong>{selectedJob.practiceCode}</strong> &bull; Location: <strong>{selectedJob.locationCode}</strong> &bull; Created {formatDateTime(selectedJob.createdAt)}
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button className="btn-cancel" onClick={handleExportReport} disabled={exporting || isProcessing || revalidating}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', fontSize: '13px' }}>
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                  <path d="M3 17C3 18.1046 3.89543 19 5 19H15C16.1046 19 17 18.1046 17 17V13M13 10L10 13M10 13L7 10M10 13V1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {exporting ? 'Exporting...' : 'Export Report'}
              </button>
              <button className="btn-primary" onClick={handleRevalidate} disabled={revalidating || isProcessing}>
                Re-validate
              </button>
            </div>
          </div>

          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            {[
              { label: 'Total Rows', value: selectedJob.totalRows || 0, color: '#111827' },
              { label: 'Valid', value: selectedJob.validRows || 0, color: '#059669' },
              { label: 'Invalid', value: selectedJob.invalidRows || 0, color: '#dc2626' },
            ].map(s => (
              <div key={s.label} style={{ padding: '16px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>{s.label}</div>
                <div style={{ fontSize: '24px', fontWeight: '600', color: s.color }}>{s.value}</div>
              </div>
            ))}
            <div style={{ padding: '16px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px' }}>Status</div>
              <span className="job-status-badge job-status-badge--large" style={getJobStatusStyle(selectedJob.status)}>
                {formatJobStatus(selectedJob.status)}
              </span>
            </div>
          </div>

          {selectedJob.failureReason && (
            <div style={{ marginBottom: '16px', padding: '12px', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '6px', color: '#dc2626', fontSize: '14px' }}>
              <strong>Failure Reason:</strong> {selectedJob.failureReason}
            </div>
          )}

          {/* Filter bar — same layout as Checks: full-width search row, error filter fixed on the right */}
          <div className="filters-section bulk-job-detail-filters">
            <div className="search-bar">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="2" />
                <path d="M15 15L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <input
                type="text"
                value={batchSearch}
                onChange={handleBatchSearchChange}
                placeholder="Search batches..."
              />
            </div>
            <div className="filter-dropdowns filter-dropdowns--error-only">
              <SearchableDropdown
                options={batchErrorTypesLoading ? [{ value: '', label: 'Loading...' }] : errorFilterOptions}
                value={filterErrors[0] || ''}
                onChange={handleFilterChange}
                placeholder="All Errors"
              />
              {filterErrors.length > 0 && (
                <button
                  type="button"
                  className="btn-cancel batch-validation-clear-errors"
                  onClick={() => { setFilterErrors([]); if (selectedJob) fetchJobBatches(selectedJob.jobId, 0, []); }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Batches table */}
          <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ fontSize: '13px', color: '#6b7280' }}>
                  {loadingBatches ? 'Loading...' : showingText}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={handlePreviousPage} disabled={currentPage === 0 || loadingBatches} className="btn-cancel">Previous</button>
                  <button onClick={handleNextPage} disabled={currentPage >= totalPages - 1 || loadingBatches} className="btn-cancel">Next</button>
                </div>
              </div>

              <div className="checks-table-wrapper batch-validation-batches-checks-table-wrapper">
                <table className="checks-table">
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb', background: '#f9fafb' }}>
                      {[
                        { label: 'Row', field: 'sheetRowNumber' },
                        { label: 'Status', field: 'rowStatus' },
                        { label: 'Batch', field: 'batchNumber' },
                        { label: 'BK Deposit Date', field: 'bkDepositDate' },
                        { label: 'Batch Date', field: 'sheetCreateDate' },
                        { label: 'Payments', field: 'payments' },
                        { label: 'Batch Type', field: null },
                        { label: 'Run', field: null },
                        { label: 'Check Number', field: 'checkNumber' },
                        { label: 'Errors', field: null },
                        { label: 'Comments', field: null },
                        { label: 'System Check', field: 'systemCheckNumber' },
                        { label: 'Actions', field: null },
                      ].map(({ label, field }) => (
                        <th key={label}
                          onClick={field ? () => handleSort(field) : undefined}
                          style={{ padding: '12px', textAlign: 'left', fontWeight: '600', whiteSpace: 'nowrap', cursor: field ? 'pointer' : 'default', userSelect: 'none' }}>
                          {label}{field && <SortArrow field={field} />}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loadingBatches ? (
                      <tr><td colSpan={13} style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading batches...</td></tr>
                    ) : displayedBatches.length === 0 ? (
                      <tr><td colSpan={13} style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                        {batches.length === 0 ? 'No batches found' : 'No batches match your search'}
                      </td></tr>
                    ) : displayedBatches.map(batch => {
                      const isPromotingThisRow = promotingBatchId === batch.stagingBatchId;
                      return (
                        <tr key={batch.stagingBatchId}
                          style={{ borderBottom: '1px solid #e5e7eb', background: batch.rowStatus === 'INVALID' ? '#fff8f8' : 'transparent' }}>
                          <td style={{ padding: '12px', whiteSpace: 'nowrap' }}>{formatRowNumber(batch.sheetRowNumber)}</td>
                          <td style={{ padding: '12px' }}>
                            <span className="job-status-badge" style={getRowStatusStyle(batch.rowStatus)}>
                              {batch.rowStatus || 'UNKNOWN'}
                            </span>
                          </td>
                          <td style={{ padding: '12px' }}>{batch.batchNumber || '-'}</td>
                          <td style={{ padding: '12px', whiteSpace: 'nowrap' }}>{batch.bkDepositDate ? formatDateUS(batch.bkDepositDate) : '-'}</td>
                          <td style={{ padding: '12px', whiteSpace: 'nowrap' }}>{batch.sheetCreateDate ? formatDateUS(batch.sheetCreateDate) : '-'}</td>
                          <td style={{ padding: '12px', whiteSpace: 'nowrap' }}>{batch.payments != null ? formatCurrency(batch.payments) : '-'}</td>
                          <td style={{ padding: '12px' }}>{batch.batchType || '-'}</td>
                          <td style={{ padding: '12px' }}>{batch.runNumber || '-'}</td>
                          <td style={{ padding: '12px' }}><TruncatedText text={batch.checkNumber} maxLength={18} /></td>
                          <td style={{ padding: '12px', maxWidth: '200px' }}>
                            <RowValidationErrorsCell
                              validationErrors={batch.validationErrors}
                              validationErrorInfo={batch.validationErrorInfo}
                            />
                          </td>
                          <td style={{ padding: '12px' }}><TruncatedText text={batch.comments} maxLength={20} /></td>
                          <td style={{ padding: '12px' }}><TruncatedText text={batch.systemCheckNumber} maxLength={18} /></td>
                          <td style={{ padding: '12px' }}>
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                              {/* View */}
                              <button title="View details"
                                style={{ padding: '5px 7px', display: 'flex', alignItems: 'center', background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', color: '#374151', cursor: 'pointer' }}
                                onClick={() => handleViewBatch(batch)}>
                                <MdOutlineVisibility size={15} />
                              </button>
                              {/* Edit */}
                              <button title="Edit batch"
                                style={{ padding: '5px 7px', display: 'flex', alignItems: 'center', background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', color: '#2563eb', cursor: isProcessing ? 'not-allowed' : 'pointer' }}
                                onClick={() => handleEditBatch(batch)} disabled={isProcessing}>
                                <MdOutlineEdit size={15} />
                              </button>
                              {/* Delete row */}
                              <button title="Delete row"
                                style={{ padding: '5px 7px', display: 'flex', alignItems: 'center', background: 'white', border: '1px solid #fecaca', borderRadius: '6px', color: '#dc2626', cursor: isProcessing ? 'not-allowed' : 'pointer' }}
                                onClick={() => handleDeleteRowClick(batch)} disabled={isProcessing || deletingRow}>
                                <Trash2 size={15} />
                              </button>
                              {/* Promote — hidden for already promoted rows */}
                              {String(batch.rowStatus || '').toUpperCase() !== 'PROMOTED' && (
                                <button
                                  title={isPromotingThisRow ? 'Promoting...' : 'Promote'}
                                  style={{
                                    padding: '5px 7px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    background: 'white',
                                    border: '1px solid #bfdbfe',
                                    borderRadius: '6px',
                                    color: '#2563eb',
                                    cursor: (isProcessing || isPromotingThisRow) ? 'not-allowed' : 'pointer',
                                    opacity: isPromotingThisRow ? 0.7 : 1,
                                  }}
                                  onClick={() => openPromoteModal(batch)}
                                  disabled={isProcessing || isPromotingThisRow || promoteModalBatch}
                                >
                                  <ArrowUpCircle size={15} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
        </div>
      )}

      {/* ── View Modal ───────────────────────────────────────────────────────── */}
      {viewingBatch && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}
          onClick={() => setViewingBatch(null)}>
          <div style={{ background: 'white', borderRadius: '10px', padding: '24px', maxWidth: '960px', width: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111827' }}>Batch Details</h3>
                <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#6b7280' }}>Row #{formatRowNumber(viewingBatch.sheetRowNumber)} &bull; {viewingBatch.batchNumber}</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span className="job-status-badge job-status-badge--emphasis" style={getRowStatusStyle(viewingBatch.rowStatus)}>
                  {viewingBatch.rowStatus || 'UNKNOWN'}
                </span>
                <button onClick={() => setViewingBatch(null)}
                  style={{ background: 'none', border: 'none', fontSize: '22px', color: '#6b7280', cursor: 'pointer', lineHeight: 1 }}>×</button>
              </div>
            </div>

            {/* Errors section at top if any */}
            {(viewingBatch.validationErrors?.length > 0 || viewingBatch.validationErrorInfo) && (
              <div style={{ marginBottom: '20px' }}>
                <ErrorsBlock validationErrors={viewingBatch.validationErrors} validationErrorInfo={viewingBatch.validationErrorInfo} />
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 24px' }}>
              <DetailRow label="Batch Number" value={viewingBatch.batchNumber} />
              <DetailRow label="GRP" value={viewingBatch.grp} />
              <DetailRow label="Status" value={viewingBatch.status} />
              <DetailRow label="INI" value={viewingBatch.ini} />
              <DetailRow label="Type" value={viewingBatch.type} />
              <DetailRow label="Batch Type" value={viewingBatch.batchType} />
              <DetailRow label="BK Deposit Date" value={viewingBatch.bkDepositDate ? formatDateUS(viewingBatch.bkDepositDate) : null} />
              <DetailRow label="Batch Date" value={viewingBatch.sheetCreateDate ? formatDateUS(viewingBatch.sheetCreateDate) : null} />
              <DetailRow label="Txns Count" value={viewingBatch.txnsCount != null ? String(viewingBatch.txnsCount) : null} />
              <DetailRow label="Check Type" value={viewingBatch.checkType} />
              <DetailRow label="Check Number" value={viewingBatch.checkNumber} />
              <DetailRow label="System Check Number" value={viewingBatch.systemCheckNumber} />
              <DetailRow label="Run Number" value={viewingBatch.runNumber} />
              <DetailRow label="EDM Number" value={viewingBatch.edmNumber} />
              <DetailRow label="Invoice Number" value={viewingBatch.invoiceNumber} />
              <DetailRow
                label="Correction Batch"
                value={(() => {
                  const v = viewingBatch.correctionBatch ?? viewingBatch.isCorrectionBatch;
                  if (v === true) return 'Yes';
                  if (v === false || v == null || v === '') return 'No';
                  const s = String(v).trim().toLowerCase();
                  return s === 'true' || s === 'yes' || s === '1' || s === 'y' ? 'Yes' : 'No';
                })()}
              />
              <DetailRow label="Batch Source" value={viewingBatch.batchSource} />
              <DetailRow label="Name" value={viewingBatch.name} />
              <DetailRow label="Charges" value={viewingBatch.charges != null ? formatCurrency(viewingBatch.charges) : null} />
              <DetailRow label="Payments" value={viewingBatch.payments != null ? formatCurrency(viewingBatch.payments) : null} />
              <DetailRow label="Adjustments" value={viewingBatch.adjustments != null ? formatCurrency(viewingBatch.adjustments) : null} />
              <DetailRow label="Total" value={viewingBatch.total != null ? formatCurrency(viewingBatch.total) : null} />
              <DetailRow label="Telephone" value={viewingBatch.telephone} />
              <DetailRow label="Last Validated" value={formatDateTime(viewingBatch.lastValidatedAt)} />
              <DetailRow label="Created At" value={formatDateTime(viewingBatch.createdAt)} />
              <DetailRow label="Description" value={viewingBatch.description} wide />
              <DetailRow label="Comments" value={viewingBatch.comments} wide />
            </div>

            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn-cancel" onClick={() => setViewingBatch(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Modal ───────────────────────────────────────────────────────── */}
      {editingBatch && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '10px', padding: '24px', maxWidth: '980px', width: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111827' }}>Edit Batch</h3>
              <button onClick={handleCancelEdit} disabled={savingEdit}
                style={{ background: 'none', border: 'none', fontSize: '22px', color: '#6b7280', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <p style={{ margin: '0 0 16px 0', fontSize: '12px', color: '#6b7280' }}>Row #{formatRowNumber(editingBatch.sheetRowNumber)} &bull; {editingBatch.batchNumber} — Saves and auto re-validates</p>

            {/* Show current errors at top */}
            {(editingBatch.validationErrors?.length > 0 || editingBatch.validationErrorInfo) && (
              <div style={{ marginBottom: '16px' }}>
                <ErrorsBlock validationErrors={editingBatch.validationErrors} validationErrorInfo={editingBatch.validationErrorInfo} />
              </div>
            )}

            <div className="batch-modal-content" style={{ marginTop: '8px' }}>
              <div className="batch-modal-row batch-modal-row--3">
                <div className="batch-modal-field">
                  <label className="batch-modal-label">GRP</label>
                  <input type="text" value={editFormData.grp} onChange={(e) => setEditFormData((p) => ({ ...p, grp: e.target.value }))} />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Status</label>
                  <input type="text" value={editFormData.status} onChange={(e) => setEditFormData((p) => ({ ...p, status: e.target.value }))} />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">INI</label>
                  <input type="text" value={editFormData.ini} onChange={(e) => setEditFormData((p) => ({ ...p, ini: e.target.value }))} />
                </div>
              </div>
              <div className="batch-modal-row batch-modal-row--3">
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Type</label>
                  <input type="text" value={editFormData.type} onChange={(e) => setEditFormData((p) => ({ ...p, type: e.target.value }))} />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">BK Deposit Date</label>
                  <USDateInput name="bkDepositDate" value={editFormData.bkDepositDate}
                    onChange={(e) => setEditFormData((p) => ({ ...p, bkDepositDate: e.target.value }))} />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Txns Count</label>
                  <input type="number" value={editFormData.txnsCount} onChange={(e) => setEditFormData((p) => ({ ...p, txnsCount: e.target.value }))} />
                </div>
              </div>

              <div style={{ marginTop: '8px', marginBottom: '4px', fontSize: '13px', fontWeight: '700', color: '#111827' }}>Batch</div>

              {/* Row 1: Batch Type | Source | Batch Date — same rules as Check Details / promote modal */}
              <div className="batch-modal-row batch-modal-row--3">
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Batch Type</label>
                  <select
                    value={editFormData.batchType}
                    onChange={(e) => handleEditFormChange('batchType', e.target.value)}
                  >
                    <option value="MANUAL">MANUAL</option>
                    <option value="AUTO">AUTO</option>
                  </select>
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Source <span style={{ color: '#dc2626' }}>*</span></label>
                  <select
                    value={editFormData.batchSource || 'Athena'}
                    onChange={(e) => handleEditFormChange('batchSource', e.target.value)}
                  >
                    <option value="Athena">Athena</option>
                    <option value="Centricity">Centricity</option>
                  </select>
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Batch Date</label>
                  <USDateInput
                    name="sheetCreateDate"
                    value={editFormData.sheetCreateDate}
                    onChange={(e) => setEditFormData((p) => ({ ...p, sheetCreateDate: e.target.value }))}
                    placeholder="MM/DD/YYYY"
                  />
                </div>
              </div>

              {/* Row 2: Run Number | Batch Number | EDM Number */}
              <div className="batch-modal-row batch-modal-row--3">
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Run Number</label>
                  <input
                    type="text"
                    value={editFormData.batchType === 'MANUAL' ? '' : editFormData.runNumber}
                    onChange={(e) => handleEditFormChange('runNumber', e.target.value)}
                    disabled={editFormData.batchType === 'MANUAL'}
                    readOnly={editFormData.batchType === 'MANUAL'}
                    placeholder={editFormData.batchType === 'MANUAL' ? '' : 'Enter run number'}
                    style={{
                      backgroundColor: editFormData.batchType === 'MANUAL' ? '#f3f4f6' : 'white',
                      cursor: editFormData.batchType === 'MANUAL' ? 'not-allowed' : 'text',
                    }}
                  />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Batch Number</label>
                  <input
                    type="text"
                    value={editFormData.batchNumber}
                    onChange={(e) => setEditFormData((p) => ({ ...p, batchNumber: e.target.value }))}
                  />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">EDM Number</label>
                  <input
                    type="text"
                    value={editFormData.edmNumber}
                    onChange={(e) => handleEditFormChange('edmNumber', e.target.value)}
                    disabled={editFormData.batchType !== 'MANUAL'}
                    readOnly={editFormData.batchType !== 'MANUAL'}
                    style={{
                      backgroundColor: editFormData.batchType !== 'MANUAL' ? '#f3f4f6' : 'white',
                      cursor: editFormData.batchType !== 'MANUAL' ? 'not-allowed' : 'text',
                    }}
                  />
                </div>
              </div>

              {/* Row 3: Invoice Number | Amount (payments) | Correction Batch */}
              <div className="batch-modal-row batch-modal-row--3">
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Invoice Number</label>
                  <input
                    type="text"
                    value={editFormData.invoiceNumber}
                    onChange={(e) => setEditFormData((p) => ({ ...p, invoiceNumber: e.target.value }))}
                  />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editFormData.payments}
                    onChange={(e) => setEditFormData((p) => ({ ...p, payments: e.target.value }))}
                  />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Correction Batch</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', paddingTop: '2px' }}>
                    <input
                      type="checkbox"
                      checked={editFormData.correctionBatch || false}
                      onChange={(e) => handleEditFormChange('correctionBatch', e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>{editFormData.correctionBatch ? 'Yes' : 'No'}</span>
                  </label>
                </div>
              </div>

              <div className="batch-modal-row batch-modal-row--3">
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Check Type</label>
                  <SearchableDropdown
                    options={[
                      { value: '', label: 'Select Check Type' },
                      ...(checkTypes || []).map((ct) => ({ value: ct.code, label: ct.name })),
                    ]}
                    value={editFormData.checkType}
                    onChange={(val) => setEditFormData((p) => ({ ...p, checkType: val }))}
                    placeholder="Select Check Type"
                    compact={true}
                    disabled={checkTypesLoading}
                  />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Check Number</label>
                  <input
                    type="text"
                    value={editFormData.checkNumber}
                    onChange={(e) => setEditFormData((p) => ({ ...p, checkNumber: e.target.value }))}
                  />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Name</label>
                  <input type="text" value={editFormData.name} onChange={(e) => setEditFormData((p) => ({ ...p, name: e.target.value }))} />
                </div>
              </div>

              <div className="batch-modal-row batch-modal-row--3">
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Telephone</label>
                  <input type="text" value={editFormData.telephone} onChange={(e) => setEditFormData((p) => ({ ...p, telephone: e.target.value }))} />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Charges</label>
                  <input type="number" step="0.01" value={editFormData.charges} onChange={(e) => setEditFormData((p) => ({ ...p, charges: e.target.value }))} />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Adjustments</label>
                  <input type="number" step="0.01" value={editFormData.adjustments} onChange={(e) => setEditFormData((p) => ({ ...p, adjustments: e.target.value }))} />
                </div>
              </div>

              <div className="batch-modal-row batch-modal-row--3">
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Total</label>
                  <input type="number" step="0.01" value={editFormData.total} onChange={(e) => setEditFormData((p) => ({ ...p, total: e.target.value }))} />
                </div>
              </div>

              <div className="batch-modal-field batch-modal-field-full">
                <label className="batch-modal-label">Description</label>
                <input type="text" value={editFormData.description} onChange={(e) => setEditFormData((p) => ({ ...p, description: e.target.value }))} />
              </div>
              <div className="batch-modal-field batch-modal-field-full">
                <label className="batch-modal-label">Comments</label>
                <textarea value={editFormData.comments} onChange={(e) => setEditFormData((p) => ({ ...p, comments: e.target.value }))} rows={3} />
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                <button type="button" className="btn-cancel" onClick={handleCancelEdit} disabled={savingEdit}>Cancel</button>
                <button type="button" className="btn-upload" onClick={handleSaveEdit} disabled={savingEdit}>
                  {savingEdit ? 'Saving & Re-validating...' : 'Save & Re-validate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Promote batch modal (review/edit payload before promote) ───────── */}
      {promoteModalBatch && promoteForm && selectedJob && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}
          onClick={closePromoteModal}>
          <div style={{ background: 'white', borderRadius: '10px', padding: '24px', maxWidth: '960px', width: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111827' }}>Promote batch</h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
                  Row #{formatRowNumber(promoteModalBatch.sheetRowNumber)} — Review and edit the payload sent to promote
                </p>
              </div>
              <button type="button" onClick={closePromoteModal} disabled={!!promotingBatchId}
                style={{ background: 'none', border: 'none', fontSize: '22px', color: '#6b7280', cursor: promotingBatchId ? 'not-allowed' : 'pointer', lineHeight: 1 }}>×</button>
            </div>

            <div className="batch-modal-content" style={{ marginTop: '8px' }}>
              <div className="batch-modal-row batch-modal-row--3">
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Check number</label>
                  <input
                    type="text"
                    value={promoteForm.checkNumber}
                    onChange={(e) => setPromoteForm((p) => ({ ...p, checkNumber: e.target.value }))}
                  />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Practice code</label>
                  <input
                    type="text"
                    value={promoteForm.practiceCode}
                    onChange={(e) => setPromoteForm((p) => ({ ...p, practiceCode: e.target.value }))}
                  />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Location code</label>
                  <input
                    type="text"
                    value={promoteForm.locationCode}
                    onChange={(e) => setPromoteForm((p) => ({ ...p, locationCode: e.target.value }))}
                  />
                </div>
              </div>

              <div style={{ marginTop: '8px', marginBottom: '4px', fontSize: '13px', fontWeight: '700', color: '#111827' }}>Batch</div>

              {/* Row 1: Batch Type | Source | Batch Date (same rules as Check Details) */}
              <div className="batch-modal-row batch-modal-row--3">
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Batch Type</label>
                  <select
                    value={promoteForm.batchType}
                    onChange={(e) => handlePromoteFormChange('batchType', e.target.value)}
                  >
                    <option value="MANUAL">MANUAL</option>
                    <option value="AUTO">AUTO</option>
                  </select>
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Source <span style={{ color: '#dc2626' }}>*</span></label>
                  <select
                    value={promoteForm.batchSource || 'Athena'}
                    onChange={(e) => handlePromoteFormChange('batchSource', e.target.value)}
                  >
                    <option value="Athena">Athena</option>
                    <option value="Centricity">Centricity</option>
                  </select>
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Batch Date</label>
                  <USDateInput
                    name="promoteBatchDate"
                    value={promoteForm.batchDate}
                    onChange={(e) => setPromoteForm((p) => ({ ...p, batchDate: e.target.value }))}
                    placeholder="MM/DD/YYYY"
                  />
                </div>
              </div>

              {/* Row 2: Run Number | Batch Number | EDM Number */}
              <div className="batch-modal-row batch-modal-row--3">
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Run Number</label>
                  <input
                    type="text"
                    value={promoteForm.batchType === 'MANUAL' ? '' : promoteForm.batchRunNumber}
                    onChange={(e) => handlePromoteFormChange('batchRunNumber', e.target.value)}
                    disabled={promoteForm.batchType === 'MANUAL'}
                    readOnly={promoteForm.batchType === 'MANUAL'}
                    placeholder={promoteForm.batchType === 'MANUAL' ? '' : 'Enter run number'}
                    style={{
                      backgroundColor: promoteForm.batchType === 'MANUAL' ? '#f3f4f6' : 'white',
                      cursor: promoteForm.batchType === 'MANUAL' ? 'not-allowed' : 'text',
                    }}
                  />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Batch Number</label>
                  <input
                    type="text"
                    value={promoteForm.batchNumber}
                    onChange={(e) => setPromoteForm((p) => ({ ...p, batchNumber: e.target.value }))}
                  />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">EDM Number</label>
                  <input
                    type="text"
                    value={promoteForm.edmNumber}
                    onChange={(e) => handlePromoteFormChange('edmNumber', e.target.value)}
                    disabled={promoteForm.batchType !== 'MANUAL'}
                    readOnly={promoteForm.batchType !== 'MANUAL'}
                    style={{
                      backgroundColor: promoteForm.batchType !== 'MANUAL' ? '#f3f4f6' : 'white',
                      cursor: promoteForm.batchType !== 'MANUAL' ? 'not-allowed' : 'text',
                    }}
                  />
                </div>
              </div>

              {/* Row 3: Invoice Number | Amount | Correction Batch */}
              <div className="batch-modal-row batch-modal-row--3">
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Invoice Number</label>
                  <input
                    type="text"
                    value={promoteForm.invoiceNumber}
                    onChange={(e) => setPromoteForm((p) => ({ ...p, invoiceNumber: e.target.value }))}
                  />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={promoteForm.batchAmount}
                    onChange={(e) => setPromoteForm((p) => ({ ...p, batchAmount: e.target.value }))}
                  />
                </div>
                <div className="batch-modal-field">
                  <label className="batch-modal-label">Correction Batch</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', paddingTop: '2px' }}>
                    <input
                      type="checkbox"
                      checked={promoteForm.correctionBatch || false}
                      onChange={(e) => handlePromoteFormChange('correctionBatch', e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>{promoteForm.correctionBatch ? 'Yes' : 'No'}</span>
                  </label>
                </div>
              </div>

              <div className="batch-modal-field batch-modal-field-full">
                <label className="batch-modal-label">Notes</label>
                <textarea
                  value={promoteForm.batchNotes}
                  onChange={(e) => setPromoteForm((p) => ({ ...p, batchNotes: e.target.value }))}
                  rows={3}
                />
              </div>
            </div>

            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
              <button type="button" className="btn-cancel" onClick={closePromoteModal} disabled={!!promotingBatchId}>Cancel</button>
              <button type="button" className="btn-upload" onClick={handleConfirmPromoteBatch} disabled={!!promotingBatchId}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <ArrowUpCircle size={18} />
                {promotingBatchId ? 'Promoting...' : 'Promote'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Row Confirmation Modal ────────────────────────────────────── */}
      {deletingBatchRow && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '10px', padding: '24px', maxWidth: '400px', width: '90%', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
            <h3 style={{ marginTop: 0, marginBottom: '12px', fontSize: '18px', fontWeight: '700', color: '#dc2626' }}>Delete Batch Row</h3>
            <p style={{ marginBottom: '8px', color: '#374151', fontSize: '14px', lineHeight: '1.5' }}>
              Are you sure you want to permanently delete this batch row?
            </p>
            <div style={{ background: '#f9fafb', borderRadius: '6px', padding: '10px 14px', marginBottom: '20px', fontSize: '13px' }}>
              <div><strong>Row #:</strong> {formatRowNumber(deletingBatchRow.sheetRowNumber)}</div>
              <div><strong>Batch #:</strong> {deletingBatchRow.batchNumber || '-'}</div>
              <div><strong>Status:</strong> {deletingBatchRow.rowStatus}</div>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button className="btn-cancel" onClick={() => setDeletingBatchRow(null)} disabled={deletingRow}>Cancel</button>
              <button
                style={{ padding: '8px 20px', background: '#dc2626', border: 'none', borderRadius: '6px', fontSize: '14px', color: 'white', cursor: deletingRow ? 'not-allowed' : 'pointer', fontWeight: '500', opacity: deletingRow ? 0.6 : 1 }}
                onClick={handleDeleteRowConfirm} disabled={deletingRow}>
                {deletingRow ? 'Deleting...' : 'Delete Row'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Job Confirmation Modal ────────────────────────────────────── */}
      {showDeleteConfirm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '10px', padding: '24px', maxWidth: '400px', width: '90%', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
            <h3 style={{ marginTop: 0, marginBottom: '12px', fontSize: '18px', fontWeight: '700', color: '#dc2626' }}>Confirm Delete Job</h3>
            <p style={{ marginBottom: '20px', color: '#374151', fontSize: '14px', lineHeight: '1.5' }}>
              Are you sure you want to delete the job "<strong>{jobToDelete?.jobName || 'Unnamed Job'}</strong>"? This will permanently delete the job and all staged batches. This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button className="btn-cancel" onClick={() => { setShowDeleteConfirm(false); setJobToDelete(null); }} disabled={deleting}>Cancel</button>
              <button
                style={{ padding: '8px 20px', background: '#dc2626', border: 'none', borderRadius: '6px', fontSize: '14px', color: 'white', cursor: deleting ? 'not-allowed' : 'pointer', fontWeight: '500', opacity: deleting ? 0.6 : 1 }}
                onClick={handleDeleteConfirm} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete Job'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchValidation;
